import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaQuestionCircle, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const ApiHelpOfferModal = ({ question, isOpen, onClose, onAccept, onDecline }) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState(null);

  if (!isOpen || !question) return null;

  // Get the appropriate message based on question
  const getHelpMessage = () => {
    const questionText = question.engagementQuestion?.toLowerCase() || '';
    
    if (questionText.includes('carbon footprint')) {
      return {
        title: t('apiHelp.carbonFootprintTitle'),
        acceptText: t('apiHelp.yesCalculateViaApi'),
        declineText: t('apiHelp.noContinueWithoutCalculation')
      };
    } else if (questionText.includes('ghg emission regulations') || questionText.includes('comply')) {
      return {
        title: t('apiHelp.ghgEmissionRegulationsTitle'),
        acceptText: t('apiHelp.yesHelpCreatePolicy'),
        declineText: t('apiHelp.noContinueWithoutPolicy')
      };
    } else if (questionText.includes('air quality')) {
      return {
        title: t('apiHelp.airQualityTitle'),
        acceptText: t('apiHelp.yesHelpCreatePolicy'),
        declineText: t('apiHelp.noContinueWithoutPolicy')
      };
    } else if (questionText.includes('energy usage') || questionText.includes('renewable energy') || questionText.includes('energy efficiency')) {
      return {
        title: t('apiHelp.energyUsageTitle'),
        acceptText: t('apiHelp.yesHelpCreatePolicy'),
        declineText: t('apiHelp.noContinueWithoutPolicy')
      };
    } else if (questionText.includes('total energy consumption') || questionText.includes('measure and report')) {
      return {
        title: t('apiHelp.totalEnergyConsumptionTitle'),
        acceptText: t('apiHelp.yesCalculateViaApi'),
        declineText: t('apiHelp.noProvideManually')
      };
    }
    
    return {
      title: t('apiHelp.defaultTitle'),
      acceptText: t('apiHelp.yesCalculateViaApi'),
      declineText: t('apiHelp.noContinueWithoutCalculation')
    };
  };

  const message = getHelpMessage();

  const handleAccept = () => {
    setSelectedOption('accept');
    if (onAccept) {
      onAccept();
    }
  };

  const handleDecline = () => {
    setSelectedOption('decline');
    if (onDecline) {
      onDecline();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4">
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <FaQuestionCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white text-xl font-semibold">{t('apiHelp.title')}</h3>
                <p className="text-green-100 text-sm">
                  {t('apiHelp.subtitle')}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="bg-transparent text-white hover:text-gray-200 transition-colors duration-200"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <p className="text-gray-700 text-base leading-relaxed mb-6">
            {message.title}
          </p>

          <div className="space-y-4">
            <button
              onClick={handleAccept}
              className={`w-full flex items-center justify-between p-4 border-2 rounded-lg transition-all duration-200 ${
                selectedOption === 'accept'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 bg-white hover:border-green-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedOption === 'accept' 
                    ? 'border-green-500 bg-green-500' 
                    : 'border-gray-300'
                }`}>
                  {selectedOption === 'accept' && (
                    <FaCheckCircle className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-gray-800 font-medium">{message.acceptText}</span>
              </div>
            </button>

            <button
              onClick={handleDecline}
              className={`w-full flex items-center justify-between p-4 border-2 rounded-lg transition-all duration-200 ${
                selectedOption === 'decline'
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 bg-white hover:border-red-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedOption === 'decline' 
                    ? 'border-red-500 bg-red-500' 
                    : 'border-gray-300'
                }`}>
                  {selectedOption === 'decline' && (
                    <FaTimesCircle className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-gray-800 font-medium">{message.declineText}</span>
              </div>
            </button>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200"
          >
{t('common.cancel')}
          </button>
          <button
            onClick={() => {
              if (selectedOption === 'accept') handleAccept();
              else if (selectedOption === 'decline') handleDecline();
            }}
            disabled={!selectedOption}
            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
{t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiHelpOfferModal;

